/**
 * Communities (gh #4872): an org-owned community, held by one `CommunityDO`
 * (`community:{community_id}`), listed in its org's registry (`org_community`, at most
 * `COMMUNITIES_PER_ORG_MAX`).
 *
 * Only the platform admins create one (Decision 1): `POST /api/admin/communities`, through the
 * audited `wayai admin communities create` CLI. The org registers the community first, then its
 * `CommunityDO` is created; a registry row whose community was never created is dropped by a
 * later registry read. A community is destroyed with its org (Decision 17), or by the platform
 * admins (`DELETE /api/admin/communities/:communityId`, `wayai admin communities destroy`): its
 * `CommunityDO` first, then its registry row, which a later registry read drops too.
 *
 * MEMBERSHIP (Decisions 3 and 21). A signed-in person joins by the community's link,
 * `{app}/join?c={community_id}&k={token}`, which its org's owners and admins read, replace and
 * turn off (`/api/communities/:communityId/join-link`). The join page shows the community from
 * `GET /api/communities/join-preview`, which needs no sign-in; `POST …/join` makes the person a
 * member and `POST …/leave` ends it. Membership grants nothing else: no hub access, no org role.
 *
 * INVITE-ONLY (gh #5042). A community's `admission` (a setting) is `link`, the default — the link
 * admits whoever holds it — or `invite`: the link then admits only the org's owners and admins and
 * the people whose verified email is on the community's access list
 * (`/api/communities/:communityId/access`), matched exactly once trimmed and lowercased. Anyone else
 * holding the link gets 409 `COMMUNITY_NOT_INVITED_REASON`, whose `details.email` is the address
 * checked (`CommunityNotInvitedDetails`). Switching to `invite` removes no member; removing an email
 * from the list removes the members it admitted, in either mode. The org's owners and admins edit the
 * list and read it, a page at a time; it shows only the emails, never whom they admitted.
 *
 * ROOMS AND MESSAGES (Decisions 4, 5, 7, 12 and 28). A community holds at most
 * `COMMUNITY_ROOMS_MAX` rooms, archived ones included, of three kinds: `discussion` (chat: members
 * post), `announcements` (the org's owners and admins and the moderators post) and `forum` (posts
 * and replies: FORUMS below). Its org's owners and admins create, rename and archive them
 * (`/api/communities/:communityId/rooms`), also while the communities switch is off.
 * A message is stored once, in its community, numbered by its room's own counter (`seq`): pages,
 * read positions and unread counts all use it, never a timestamp. Its author edits it (shown as
 * edited) and deletes it at any time, leaving a tombstone. A page leaves out the messages of
 * authors the reader blocks; unread counts do not (Decision 7).
 *
 * FORUMS (Decisions 26 and 29). A forum room's messages are posts (a title and a text) and replies
 * one level under a post; both are sent, edited and deleted as messages, under the same limits,
 * and both take the room's `seq`, so the room keeps one unread count. Posts are listed by their
 * latest activity (`last_activity_seq`: the post itself, or its newest reply), newest first; a
 * post with activity after the reader's read position in the room is new. A deleted post's
 * replies stop showing. The post's author, a moderator or an owner or admin of the org marks one
 * reply as the answer, and the list can hold only the posts with none (Unanswered) or those whose
 * title holds a search, ignoring case and accents.
 *
 * STATS (Decision 28). The org's owners and admins read the community's members, joins and new
 * accounts by the join link's source, and its messages per day, over the last 7 and 30 days, and
 * its course completions per lesson.
 *
 * MODERATION (Decisions 2 and 9). The org's owners and admins are the community's admins; they
 * appoint moderators among its members and dismiss them (`/api/communities/:communityId/moderators`).
 * Admins outrank moderators, who outrank members, and no one acts on a person of their own rank or
 * above: a moderator never on an admin or another moderator. Moderators and admins delete any
 * message, remove a member, ban a person (a ban refuses their rejoining by the link, and can delete
 * every message they wrote) and lift a ban; lock a forum post; pin up to
 * `COMMUNITY_PINS_PER_ROOM_MAX` messages or posts per room; mark a post's answer; and post in
 * announcements rooms. Every moderation is logged (`/mod-log`, at most `COMMUNITY_MOD_ACTIONS_MAX`
 * entries, the oldest dropped). Admins also set the community's rules and slow mode, which
 * moderators and admins are not held to, and list their org's communities
 * (`/api/communities/orgs/:orgId`), also while the communities switch is off.
 *
 * REPORTS AND BLOCKS (Decisions 9 and 10). A member reports a message or a person through the
 * threads surface's report (`POST /api/threads/reports`, `people-reports.ts`), which reaches the
 * WayAI team and, without the reporter's identity, the moderators' inbox
 * (`/api/communities/:communityId/reports`, at most `COMMUNITY_REPORTS_MAX`, the oldest dropped);
 * a report about a moderator or an owner or admin of the org reaches only the WayAI team, and the
 * inbox leaves out every report about a person who holds either role when it is read. Moderators
 * and admins mark a report resolved once they have handled it. A member blocks another from the
 * community (`POST /api/contacts/blocks` with `community_id`), and pages then leave out the
 * blocked person's messages.
 *
 * LIVE (Decision 6). A client with a community open holds one WebSocket to it, and none while it
 * has none open: it asks `POST /api/communities/:communityId/live-ticket` for a single-use ticket,
 * good for `COMMUNITY_LIVE_TICKET_TTL_SECONDS`, and opens `/communities/:communityId/live?ticket=…`
 * on the API's origin with it (`wss://`). Every change to a room's messages reaches each socket open
 * on the community as a frame (`CommunityLiveFrame`) that carries the change and its author's id, so
 * the client hides the frames of the authors it blocks (Decision 10). Keepalive is the per-user
 * socket's ping (`WS_CLIENT_PING_FRAME`, answered with `WS_SERVER_PONG_FRAME` without waking the
 * community). The server sends nothing to a socket past `COMMUNITY_LIVE_SOCKET_MAX_AGE_SECONDS`: it
 * closes it instead, at the community's next frame or the socket's next message, so a client
 * reconnects before its hour. The server closes a socket with one of the `COMMUNITY_LIVE_CLOSE_*`
 * codes, and a client that reconnects fills the gap with an `after` page.
 *
 * NOTIFICATIONS (Decisions 8, 28 and 29). A community pushes only to the push tokens registered with
 * `supports_communities` (`registerPushTokenBody`), and its push's `data` is `CommunityPushData`. An
 * announcement reaches every active member who has not muted the community: a push, or, for a
 * member with no such token, an email unless they turned the community's emails off; a community
 * posts at most `COMMUNITY_ANNOUNCEMENTS_PER_DAY` announcements a UTC day, and the next one is
 * refused (`COMMUNITY_ANNOUNCEMENTS_LIMIT`). A reply to a forum post pushes the post's author once,
 * then not again until they open the post, unless they muted the community, wrote the reply, or
 * block its author. A member mutes and unmutes a community (`PUT …/mute`; their head's `muted`). Each
 * email carries a link to `COMMUNITY_EMAIL_UNSUBSCRIBE_PAGE_PATH` on the web app, whose button
 * calls `POST /api/communities/email-unsubscribe?token=…`, which needs no sign-in, and the
 * one-click unsubscribe headers (RFC 8058) that mail clients POST to that same address. Chat rooms
 * never push.
 *
 * IMAGES (the Caps table: one per message, as threads). A message — a chat message, an announcement,
 * a forum post or a reply — carries at most one image (`attachment` on its send), at most
 * `COMMUNITY_ATTACHMENT_MAX_BYTES`, of a type a client draws in place (`COMMUNITY_ATTACHMENT_TYPES`,
 * read from its name: never SVG); with one, its text may be empty. It counts against its sender's
 * attachment storage, the same per-person quota a thread's files count against
 * (`PEOPLE_ATTACHMENT_STORAGE_PER_USER_BYTES`: past it the send answers 429, `details.limit`
 * `PEOPLE_ATTACHMENT_STORAGE_LIMIT`). Messages carry it by its name, type and size; its bytes open
 * only at a URL `POST /api/communities/:communityId/attachments/urls` mints for a reader of the
 * community (an active member, or an owner or admin of its org), good for
 * `COMMUNITY_ATTACHMENT_URL_TTL_SECONDS`, and only while the communities surface is on. Deleting
 * the message — its author, a moderator, a ban's delete-all, the WayAI team — deletes the image
 * and frees its sender's storage; so does the community's destruction.
 *
 * COURSES (Decisions 25 and 30) are `community-courses.ts`'s: a community's courses, their authoring
 * and reading, and each member's progress. Their tab shows on a community's head
 * (`CommunityHead.courses`) once it has a published course, under the label its admins set
 * (`courses_label`, a setting).
 */

import { z } from 'zod';
import { optionalTrimmedText, uuidSchema } from './common';
import { CONTACT_EMAIL_MAX } from './contact-book';
import { peopleUserIdSchema } from './people-contacts';
import type { CommunityReportTarget, PeopleReportAttachment, PeopleReportReason } from './people-reports';
import type { CommunityCourseStats, CommunityCoursesTab } from './community-courses';
import {
  PEOPLE_THREAD_ATTACHMENT_MAX_BYTES,
  PEOPLE_THREAD_ATTACHMENT_URL_TTL_SECONDS,
  PEOPLE_THREAD_INLINE_IMAGE_TYPES,
  peopleThreadAttachmentUploadSchema,
  type PeopleThreadAttachment,
  type PeopleThreadAttachmentUrl,
} from './people-threads';

// =============================================================================
// LIMITS
// =============================================================================

/** The communities one org owns at most (Decision 27; the Caps table). */
export const COMMUNITIES_PER_ORG_MAX = 10;
/** The longest community name, in characters. */
export const COMMUNITY_NAME_MAX_LENGTH = 100;

/**
 * The `details.reason` of the 409 a create gets when the org already owns
 * `COMMUNITIES_PER_ORG_MAX` communities.
 */
export const COMMUNITIES_PER_ORG_REASON = 'communities_per_org';

/**
 * The `details.reason` of the 409 a create gets once a deletion of the org has closed its
 * community registry (#4872 owner-delegated decision 3). The registry is never reopened.
 */
export const COMMUNITY_REGISTRY_CLOSED_REASON = 'community_registry_closed';

/** The active members one community holds at most (the Caps table). */
export const COMMUNITY_MEMBERS_MAX = 10_000;
/** The communities one person is an active member of at most (the Caps table). */
export const COMMUNITIES_PER_PERSON_MAX = 50;

/** The `details.reason` of the 409 a join gets when the community holds `COMMUNITY_MEMBERS_MAX` members. */
export const COMMUNITY_FULL_REASON = 'community_full';
/** The `details.reason` of the 409 a join gets when the person is in `COMMUNITIES_PER_PERSON_MAX` communities. */
export const COMMUNITIES_PER_PERSON_REASON = 'communities_per_person';

/**
 * How a community admits the people holding its link (gh #5042 Decision 1): `link`, the default,
 * admits them all; `invite` only the org's owners and admins and the emails on its access list.
 */
export const COMMUNITY_ADMISSIONS = ['link', 'invite'] as const;
export type CommunityAdmission = (typeof COMMUNITY_ADMISSIONS)[number];

/** The emails one community's access list holds at most: its member cap (gh #5042). */
export const COMMUNITY_ACCESS_MAX = COMMUNITY_MEMBERS_MAX;
/** The emails one add to an access list takes at most. */
export const COMMUNITY_ACCESS_ADD_MAX = 500;
/** The entries one page of an access list reads (≤ 500 emails of `CONTACT_EMAIL_MAX` characters: the 32 MiB rule). */
export const COMMUNITY_ACCESS_PAGE_SIZE = 500;
/** The `details.reason` of the 409 an add gets when it would take the list past `COMMUNITY_ACCESS_MAX`; nothing is added. */
export const COMMUNITY_ACCESS_FULL_REASON = 'community_access_full';
/**
 * The `details.reason` of the 409 a join of an invite-only community gets when the caller is not an
 * owner or admin of its org and its access list does not hold their verified email
 * (`CommunityNotInvitedDetails`).
 */
export const COMMUNITY_NOT_INVITED_REASON = 'community_not_invited';

/** The longest join-link source tag (`?src=`), in characters (Decision 28). */
export const COMMUNITY_JOIN_SOURCE_MAX_LENGTH = 32;
/** The longest join-flow token a join takes; one the preview mints is shorter. */
export const COMMUNITY_JOIN_FLOW_TOKEN_MAX_LENGTH = 128;

/** The rooms one community holds at most, archived ones included (the Caps table). */
export const COMMUNITY_ROOMS_MAX = 20;
/** The longest room name, in characters. */
export const COMMUNITY_ROOM_NAME_MAX_LENGTH = 100;
/** The `details.reason` of the 409 a room's creation gets when the community holds `COMMUNITY_ROOMS_MAX` rooms. */
export const COMMUNITY_ROOMS_REASON = 'rooms_per_community';

/** The longest message body, in characters (as threads'). */
export const COMMUNITY_MESSAGE_MAX_LENGTH = 4_000;
/**
 * The messages one page reads (≤ 50 × 4,000 characters per answer: the 32 MiB rule). A page
 * leaves out the messages of authors the reader blocks, so it may hold fewer, while its cursor
 * still moves past every message it read.
 */
export const COMMUNITY_MESSAGES_PAGE_SIZE = 50;
/**
 * The messages, tombstones excluded, one community holds at most (the Caps table). At the cap a
 * send answers 429 (`details.limit`: `COMMUNITY_LIVE_MESSAGES_LIMIT`) until an archival design
 * exists; deleting a message frees its place.
 */
export const COMMUNITY_LIVE_MESSAGES_MAX = 500_000;
/** Messages one community takes per second, from all its members together. */
export const COMMUNITY_MESSAGES_PER_SECOND = 5;
/** Messages one member sends in one community per UTC day. */
export const COMMUNITY_MEMBER_MESSAGES_PER_DAY = 500;
/** The same limit in a member's first day: the 24 hours after they joined. */
export const COMMUNITY_NEW_MEMBER_MESSAGES_PER_DAY = 20;

/** The largest image one message carries, in bytes: a thread file's cap (the Caps table, "as threads"). */
export const COMMUNITY_ATTACHMENT_MAX_BYTES = PEOPLE_THREAD_ATTACHMENT_MAX_BYTES;
/** The image types a message takes, read from the file's name: those a client draws in place, never SVG. */
export const COMMUNITY_ATTACHMENT_TYPES: readonly string[] = PEOPLE_THREAD_INLINE_IMAGE_TYPES;
/** The `details.reason` of the 400 a message's file answers when its name gives none of those types. */
export const COMMUNITY_ATTACHMENT_TYPE_REASON = 'attachment_type';
/** How long an image's URL opens it, in seconds: a thread file's. */
export const COMMUNITY_ATTACHMENT_URL_TTL_SECONDS = PEOPLE_THREAD_ATTACHMENT_URL_TTL_SECONDS;
/** Image URLs one request mints at most: a replies page's worth (its replies, its post and its answer). */
export const COMMUNITY_ATTACHMENT_URLS_MAX = COMMUNITY_MESSAGES_PAGE_SIZE + 2;

/**
 * The `details.reason` of the 409 a message gets in a room that takes no such message from the
 * caller: an announcements room from anyone but the org's owners and admins and the moderators, a
 * forum room a message that is neither a post nor a reply, and any other room a post or a reply.
 * A posts or replies page of a room that is not a forum, and a messages page of one that is, get
 * it too.
 */
export const COMMUNITY_ROOM_KIND_REASON = 'room_kind';
/** The `details.reason` of the 409 a message or an edit gets in an archived room. */
export const COMMUNITY_ROOM_ARCHIVED_REASON = 'room_archived';
/**
 * The `details.reason` of the 409 an owner or admin of the community's org gets on a message
 * before joining: they read every room as they are, and post after joining (Decision 2).
 */
export const COMMUNITY_NOT_JOINED_REASON = 'not_joined';

/** The `details.limit` of each 429 a message can get. */
export const COMMUNITY_RATE_LIMIT = 'community_rate';
export const COMMUNITY_DAILY_LIMIT = 'messages_per_day';
export const COMMUNITY_SLOW_MODE_LIMIT = 'slow_mode';
export const COMMUNITY_LIVE_MESSAGES_LIMIT = 'live_messages';
/** The 429 of an announcement past `COMMUNITY_ANNOUNCEMENTS_PER_DAY` in a UTC day. */
export const COMMUNITY_ANNOUNCEMENTS_LIMIT = 'announcements_per_day';

/**
 * Announcements a community posts in one UTC day, across its announcements rooms (the Caps table):
 * each reaches every member's phone or inbox, so the next one that day is refused. Deleting one
 * does not give its place back.
 */
export const COMMUNITY_ANNOUNCEMENTS_PER_DAY = 3;

/** The longest forum post title, in characters (the Caps table). */
export const COMMUNITY_POST_TITLE_MAX_LENGTH = 200;
/** The characters of a post's text its listing shows (`CommunityPostListing.preview`). */
export const COMMUNITY_POST_PREVIEW_LENGTH = 200;
/**
 * The posts one posts page reads (≤ 50 listings of a 200-character title and preview: the 32 MiB
 * rule). Like a messages page, it leaves out the posts of authors the reader blocks, while its
 * cursor moves past every post it read. A replies page reads `COMMUNITY_MESSAGES_PAGE_SIZE`.
 */
export const COMMUNITY_POSTS_PAGE_SIZE = 50;
/** The longest title search (`q`), in characters (the Caps table). */
export const COMMUNITY_POST_SEARCH_MAX_LENGTH = 100;

/**
 * The `details.reason` of the 409 a message gets when the message it names as a post is not one:
 * a reply to a reply (replies are one level deep, Decision 26), or a title given to a message
 * that is not a post.
 */
export const COMMUNITY_NOT_A_POST_REASON = 'not_a_post';
/** The `details.reason` of the 409 a reply to a locked post gets. */
export const COMMUNITY_POST_LOCKED_REASON = 'post_locked';

/** A membership's status (`community_member.status`). */
export const COMMUNITY_MEMBER_STATUSES = ['active', 'left', 'removed', 'banned'] as const;
export type CommunityMemberStatus = (typeof COMMUNITY_MEMBER_STATUSES)[number];

/**
 * A member's role in their community (`community_member.role`, Decision 2): a moderator, appointed
 * by the org's owners and admins, or a member. A new membership starts as a member.
 */
export const COMMUNITY_MEMBER_ROLES = ['moderator', 'member'] as const;
export type CommunityMemberRole = (typeof COMMUNITY_MEMBER_ROLES)[number];

/**
 * The viewer's standing in a community they belong to, highest first: an owner or admin of the
 * community's org (`admin`, Decision 2: never a hub admin or team user of it), a moderator, or a
 * member.
 */
export const COMMUNITY_VIEWER_ROLES = ['admin', 'moderator', 'member'] as const;
export type CommunityViewerRole = (typeof COMMUNITY_VIEWER_ROLES)[number];

/** The messages or forum posts one room pins at most (the Caps table). */
export const COMMUNITY_PINS_PER_ROOM_MAX = 3;
/** The `details.reason` of the 409 a pin gets when its room already pins `COMMUNITY_PINS_PER_ROOM_MAX`. */
export const COMMUNITY_PINS_REASON = 'pins_per_room';

/**
 * The `details.reason` of the 409 a moderation gets when its actor does not outrank the person it
 * acts on (Decision 2: an admin outranks a moderator, who outranks a member; no one acts on their
 * own rank or above).
 */
export const COMMUNITY_RANK_REASON = 'rank';

/** The longest rules text, in characters. */
export const COMMUNITY_RULES_MAX_LENGTH = 4_000;
/** The longest slow mode, in seconds (6 hours); 0 turns it off. */
export const COMMUNITY_SLOW_MODE_MAX_SECONDS = 21_600;
/** The longest Courses tab label, in characters, so the Rooms | Courses switch fits on a phone (Decision 30). */
export const COMMUNITY_COURSES_LABEL_MAX_LENGTH = 16;

/** The moderation log's entries one community keeps; past it the oldest go (the Caps table). */
export const COMMUNITY_MOD_ACTIONS_MAX = 10_000;
/** The moderation log's entries one page reads (the Caps table). */
export const COMMUNITY_MOD_ACTIONS_PAGE_SIZE = 50;
/** The people one moderators or bans page lists (the Caps table's members page). */
export const COMMUNITY_PEOPLE_PAGE_SIZE = 100;

/** The reports one community's inbox keeps; past it the oldest go (the Caps table). */
export const COMMUNITY_REPORTS_MAX = 10_000;
/** The reports one inbox page reads (the Caps table). */
export const COMMUNITY_REPORTS_PAGE_SIZE = 50;

/** What a moderation log entry records (`CommunityModAction.action`). */
export const COMMUNITY_MOD_ACTION_KINDS = [
  'delete_message',
  'remove_member',
  'ban_member',
  'lift_ban',
  'lock_post',
  'unlock_post',
  'pin_message',
  'unpin_message',
  'appoint_moderator',
  'dismiss_moderator',
] as const;
export type CommunityModActionKind = (typeof COMMUNITY_MOD_ACTION_KINDS)[number];

/** How long a live ticket opens a socket after it is minted, in seconds. */
export const COMMUNITY_LIVE_TICKET_TTL_SECONDS = 60;
/** A live ticket's prefix: `{prefix}_` and 32 random bytes, base64url without padding. */
export const COMMUNITY_LIVE_TICKET_PREFIX = 'cst';
/**
 * The longest a live socket is sent anything, in seconds (Decision 6): past it, the server closes it
 * (`COMMUNITY_LIVE_CLOSE_EXPIRED`) rather than send it a frame, so a session that can no longer
 * mint a ticket — signed out — gets nothing more after its hour.
 */
export const COMMUNITY_LIVE_SOCKET_MAX_AGE_SECONDS = 3_600;
/** The live sockets one community holds open at most (the Caps table); past it an upgrade is refused with 429. */
export const COMMUNITY_LIVE_SOCKETS_MAX = 1_000;
/**
 * The live sockets one person holds open on one community at most (a tab or a device each), so no
 * one fills `COMMUNITY_LIVE_SOCKETS_MAX` alone; past it their upgrade is refused with 429.
 */
export const COMMUNITY_LIVE_SOCKETS_PER_PERSON_MAX = 5;

/**
 * The close codes of a community's live socket. Codes of the per-user socket's registry
 * (`live-updates.ts`) are never reused here.
 *   - `EXPIRED`: the socket reached `COMMUNITY_LIVE_SOCKET_MAX_AGE_SECONDS`; reconnect with a new ticket.
 *   - `ENDED`: the membership the socket's holder had ended — they left, were removed or banned, or
 *     their account was deleted — or the community was destroyed. Read the heads again; a new ticket
 *     is refused (404) to anyone who no longer reads the community, and still given to an owner or
 *     admin of its org, who reads it without joining.
 *   - `OFF`: the communities surface was turned off; do not reconnect until it answers again.
 */
export const COMMUNITY_LIVE_CLOSE_EXPIRED = 4101;
export const COMMUNITY_LIVE_CLOSE_ENDED = 4102;
export const COMMUNITY_LIVE_CLOSE_OFF = 4103;

/** The days the community's stats count back (the Caps table's stats window). */
export const COMMUNITY_STATS_DAYS = 30;
/** The join-link sources the stats name, most joins first; the totals count them all. */
export const COMMUNITY_STATS_SOURCES_MAX = 100;

// =============================================================================
// REQUEST
// =============================================================================

export const communityNameSchema = z.string().trim().min(1).max(COMMUNITY_NAME_MAX_LENGTH);

/** POST /api/admin/communities — a new community owned by the org `org_id`. */
export const adminCreateCommunityBody = z
  .object({ org_id: uuidSchema, name: communityNameSchema })
  .strict();

export type AdminCreateCommunityBody = z.input<typeof adminCreateCommunityBody>;

/** A route on one community: `/api/communities/:communityId/…`. */
export const communityIdParam = z.object({ communityId: uuidSchema });

/**
 * A join link's token (`k`): 128 random bits as 32 lowercase hex characters. The link is posted
 * publicly; the token's job is replacement and disabling, not secrecy (#4872 owner-delegated
 * decision 7).
 */
export const communityJoinTokenSchema = z.string().regex(/^[0-9a-f]{32}$/);

/**
 * A join link's source tag (`?src=`, Decision 28), recorded with the membership: at most
 * `COMMUNITY_JOIN_SOURCE_MAX_LENGTH` characters of `[a-z0-9_-]`.
 */
export const communityJoinSourceSchema = z
  .string()
  .regex(new RegExp(`^[a-z0-9_-]{1,${COMMUNITY_JOIN_SOURCE_MAX_LENGTH}}$`));

/** GET /api/communities/join-preview — the community a join link names. */
export const communityJoinPreviewQuery = z.object({ c: uuidSchema, k: communityJoinTokenSchema });

/**
 * POST /api/communities/:communityId/join. `token` is the link's `k`; `src` its source tag.
 * `flow_token` is the `flow_token` of the FIRST preview the join page fetched, carried through
 * sign-up: the membership records `account_new` only when it shows the account did not exist
 * yet when that preview was served (#4872 owner-delegated decision 8). One that is missing,
 * invalid, expired or another community's records `false`; it never refuses the join.
 */
export const communityJoinBody = z
  .object({
    token: communityJoinTokenSchema,
    src: communityJoinSourceSchema.optional(),
    flow_token: z.string().max(COMMUNITY_JOIN_FLOW_TOKEN_MAX_LENGTH).optional(),
  })
  .strict();

export type CommunityJoinBody = z.input<typeof communityJoinBody>;

/** A room's kind (Decisions 4 and 26). It never changes. */
export const communityRoomKindSchema = z.enum(['discussion', 'forum', 'announcements']);
export type CommunityRoomKind = z.infer<typeof communityRoomKindSchema>;

export const communityRoomNameSchema = z.string().trim().min(1).max(COMMUNITY_ROOM_NAME_MAX_LENGTH);

/** A route on one room: `/api/communities/:communityId/rooms/:roomId/…`. */
export const communityRoomIdParam = z.object({ communityId: uuidSchema, roomId: uuidSchema });

/** A route on one message: `/api/communities/:communityId/rooms/:roomId/messages/:messageId`. */
export const communityMessageIdParam = z.object({ communityId: uuidSchema, roomId: uuidSchema, messageId: uuidSchema });

/**
 * POST /api/communities/:communityId/rooms — an org admin's new room. `client_id` makes a retried
 * create idempotent: a create naming the `client_id` of a room already made answers that room as it
 * stands and makes none.
 */
export const communityRoomCreateBody = z
  .object({ kind: communityRoomKindSchema, name: communityRoomNameSchema, client_id: uuidSchema.optional() })
  .strict();

export type CommunityRoomCreateBody = z.input<typeof communityRoomCreateBody>;

/** PATCH /api/communities/:communityId/rooms/:roomId — an org admin renames a room, or archives it or brings it back. */
export const communityRoomUpdateBody = z
  .object({ name: communityRoomNameSchema.optional(), archived: z.boolean().optional() })
  .strict()
  .refine((b) => b.name !== undefined || b.archived !== undefined, 'nothing to change');

export type CommunityRoomUpdateBody = z.input<typeof communityRoomUpdateBody>;

/**
 * GET /api/communities/:communityId/rooms/:roomId/messages — one page of
 * `COMMUNITY_MESSAGES_PAGE_SIZE` messages: the newest with neither cursor; those before `before`
 * (a `seq`), toward older ones; or those after `after`, toward newer ones — the gap a client
 * fills after it reconnects. Never both.
 */
export const communityMessagesQuery = z
  .object({
    before: z.coerce.number().int().min(1).optional(),
    after: z.coerce.number().int().min(0).optional(),
  })
  .refine((q) => q.before === undefined || q.after === undefined, 'before and after cannot be combined');

/** A message's text: at most `COMMUNITY_MESSAGE_MAX_LENGTH` characters, not only whitespace. */
export const communityMessageBodySchema = z
  .string()
  .max(COMMUNITY_MESSAGE_MAX_LENGTH)
  .refine((body) => body.trim().length > 0, 'must not be empty');

/** A forum post's title: at most `COMMUNITY_POST_TITLE_MAX_LENGTH` characters, trimmed, not empty. */
export const communityPostTitleSchema = z.string().trim().min(1).max(COMMUNITY_POST_TITLE_MAX_LENGTH);

/**
 * A message's image as its send carries it: its name and its bytes in base64 (a `data:` URL's prefix
 * is accepted), as a thread's file travels. The server reads its type from the name — one of
 * `COMMUNITY_ATTACHMENT_TYPES`, else 400 (`COMMUNITY_ATTACHMENT_TYPE_REASON`) — and its size from the
 * bytes, at most `COMMUNITY_ATTACHMENT_MAX_BYTES` (else 413).
 */
export const communityAttachmentUploadSchema = peopleThreadAttachmentUploadSchema;

/**
 * POST /api/communities/:communityId/rooms/:roomId/messages. `client_message_id` makes a retried
 * send idempotent: a second send with the same id from the same author in the same room answers
 * the first message, and stores no second image. In a forum room a message is a post, with a
 * `title`, or a reply to one, with the post's id as `parent_message_id` (Decision 26); in any other
 * room it has neither. A message carries text, one image (`attachment`), or both: its text may be
 * empty only beside an image.
 */
export const communitySendBody = z
  .object({
    body: z.string().max(COMMUNITY_MESSAGE_MAX_LENGTH).default(''),
    client_message_id: uuidSchema.optional(),
    title: communityPostTitleSchema.optional(),
    parent_message_id: uuidSchema.optional(),
    attachment: communityAttachmentUploadSchema.optional(),
  })
  .strict()
  .refine((b) => b.title === undefined || b.parent_message_id === undefined, 'a post is not a reply: send a title or a parent_message_id, not both')
  .refine((b) => b.body.trim().length > 0 || b.attachment !== undefined, { message: 'must not be empty', path: ['body'] });

export type CommunitySendBody = z.input<typeof communitySendBody>;

/**
 * PATCH /api/communities/:communityId/rooms/:roomId/messages/:messageId — its author's new text,
 * and for a forum post, optionally, its new title (Decision 28).
 */
export const communityEditBody = z
  .object({ body: communityMessageBodySchema, title: communityPostTitleSchema.optional() })
  .strict();

export type CommunityEditBody = z.input<typeof communityEditBody>;

/** A route on one forum post: `/api/communities/:communityId/rooms/:roomId/posts/:postId/…`. */
export const communityPostIdParam = z.object({ communityId: uuidSchema, roomId: uuidSchema, postId: uuidSchema });

/**
 * GET /api/communities/:communityId/rooms/:roomId/posts — one page of `COMMUNITY_POSTS_PAGE_SIZE`
 * posts, latest activity first: the newest with no cursor, or those whose latest activity is
 * before `before_activity` (a `last_activity_seq`). `q`, trimmed, keeps only the posts whose title
 * holds it, ignoring case and accents (`%` and `_` match themselves; empty is no search); `unanswered`
 * only those with no answer (Decision 29).
 */
export const communityPostsQuery = z.object({
  before_activity: z.coerce.number().int().min(1).optional(),
  q: optionalTrimmedText(COMMUNITY_POST_SEARCH_MAX_LENGTH),
  unanswered: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
});

/**
 * PUT /api/communities/:communityId/rooms/:roomId/posts/:postId/answer — the post's author, a
 * moderator or an owner or admin of the org marks `reply_id`, one of the post's replies, as its answer, or clears
 * it with null (Decision 29).
 */
export const communityAnswerBody = z.object({ reply_id: uuidSchema.nullable() }).strict();

export type CommunityAnswerBody = z.input<typeof communityAnswerBody>;

/** POST /api/communities/:communityId/rooms/:roomId/read — everything up to `seq` has been read. */
export const communityReadBody = z.object({ seq: z.number().int().min(0) }).strict();

export type CommunityReadBody = z.input<typeof communityReadBody>;

/** POST /api/communities/:communityId/attachments/urls — URLs for these images of the community. */
export const communityAttachmentUrlsBody = z
  .object({ attachment_ids: z.array(uuidSchema).min(1).max(COMMUNITY_ATTACHMENT_URLS_MAX) })
  .strict();

export type CommunityAttachmentUrlsBody = z.input<typeof communityAttachmentUrlsBody>;

/** A route on one person in a community: `/api/communities/:communityId/{moderators,members,bans}/:userId`. */
export const communityPersonParam = z.object({ communityId: uuidSchema, userId: peopleUserIdSchema });

/** A route on one org's communities: `/api/communities/orgs/:orgId`. */
export const communityOrgParam = z.object({ orgId: uuidSchema });

/**
 * GET /api/communities/:communityId/{moderators,bans} — one page of `COMMUNITY_PEOPLE_PAGE_SIZE`
 * people, by user id: the first with no cursor, or those after `after` (a user id).
 */
export const communityPeopleQuery = z.object({ after: peopleUserIdSchema.optional() });

/**
 * A community's name for its Courses tab (Decision 30): trimmed, at most
 * `COMMUNITY_COURSES_LABEL_MAX_LENGTH` characters, shown as typed; empty for the clients' locale default.
 */
export const communityCoursesLabelSchema = z.string().trim().max(COMMUNITY_COURSES_LABEL_MAX_LENGTH);

/** A community's admission (`COMMUNITY_ADMISSIONS`). */
export const communityAdmissionSchema = z.enum(COMMUNITY_ADMISSIONS);

/**
 * PATCH /api/communities/:communityId/settings — an org admin's rules (trimmed; empty or null
 * clears them), slow mode (the seconds a member waits between two messages in one room, 0 off),
 * the Courses tab's label (empty for the default) and the admission (gh #5042: switching to
 * `invite` removes no member; a suspended account's change carrying it is refused whole).
 */
export const communitySettingsBody = z
  .object({
    rules: z.string().trim().max(COMMUNITY_RULES_MAX_LENGTH).nullable().optional()
      .transform((rules) => (rules === '' ? null : rules)),
    slow_mode_seconds: z.number().int().min(0).max(COMMUNITY_SLOW_MODE_MAX_SECONDS).optional(),
    courses_label: communityCoursesLabelSchema.optional(),
    admission: communityAdmissionSchema.optional(),
  })
  .strict()
  .refine(
    (b) => b.rules !== undefined || b.slow_mode_seconds !== undefined || b.courses_label !== undefined || b.admission !== undefined,
    'nothing to change',
  );

export type CommunitySettingsBody = z.input<typeof communitySettingsBody>;

/**
 * An email on a community's access list (gh #5042 Decision 2): trimmed and lowercased — the one rule
 * a join's verified email is matched by, exactly (no `+tag` or dot folding) — and bounded as a
 * contact's email is.
 */
export const communityAccessEmailSchema = z.string().trim().toLowerCase().max(CONTACT_EMAIL_MAX).email();

/**
 * POST /api/communities/:communityId/access — an org admin adds 1 to `COMMUNITY_ACCESS_ADD_MAX`
 * emails; one already listed, or repeated, is added once.
 */
export const communityAccessAddBody = z
  .object({ emails: z.array(communityAccessEmailSchema).min(1).max(COMMUNITY_ACCESS_ADD_MAX) })
  .strict();

export type CommunityAccessAddBody = z.input<typeof communityAccessAddBody>;

/**
 * GET /api/communities/:communityId/access — one page of `COMMUNITY_ACCESS_PAGE_SIZE` entries, oldest
 * first: the first with no cursor, or those after `cursor` (the page before's `next_cursor`).
 */
export const communityAccessQuery = z.object({ cursor: z.string().min(1).max(256).optional() });

/** DELETE /api/communities/:communityId/access/:accessId — one entry of the access list. */
export const communityAccessIdParam = z.object({ communityId: uuidSchema, accessId: uuidSchema });

/**
 * PUT /api/communities/:communityId/bans/:userId — a moderator or admin bans the person; with
 * `delete_messages`, every message they wrote in the community is deleted too (Decision 9).
 */
export const communityBanBody = z.object({ delete_messages: z.boolean().optional() }).strict();

export type CommunityBanBody = z.input<typeof communityBanBody>;

/** PUT /api/communities/:communityId/rooms/:roomId/posts/:postId/lock — lock or unlock a forum post. */
export const communityLockBody = z.object({ locked: z.boolean() }).strict();

export type CommunityLockBody = z.input<typeof communityLockBody>;

/** PUT /api/communities/:communityId/rooms/:roomId/messages/:messageId/pin — pin or unpin a message or post. */
export const communityPinBody = z.object({ pinned: z.boolean() }).strict();

export type CommunityPinBody = z.input<typeof communityPinBody>;

/**
 * GET /api/communities/:communityId/mod-log — one page of `COMMUNITY_MOD_ACTIONS_PAGE_SIZE`
 * entries, newest first: the newest with no cursor, or those before `before` (an `action_id`).
 */
export const communityModLogQuery = z.object({ before: z.coerce.number().int().min(1).optional() });

/**
 * GET /api/communities/:communityId/reports — the moderators' inbox, newest first: `before` is the
 * `next_before` of the page before; `pending` keeps the reports not yet resolved.
 */
export const communityReportsQuery = communityModLogQuery.extend({
  pending: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
});

/** POST /api/communities/:communityId/reports/:reportId/resolve */
export const communityReportIdParam = z.object({ communityId: uuidSchema, reportId: uuidSchema });

/** A live ticket: `COMMUNITY_LIVE_TICKET_PREFIX`, `_` and 32 random bytes, base64url without padding. */
export const communityLiveTicketSchema = z.string().regex(new RegExp(`^${COMMUNITY_LIVE_TICKET_PREFIX}_[A-Za-z0-9_-]{43}$`));

/** GET /communities/:communityId/live — the socket's upgrade, outside `/api/*`: its one credential is the ticket. */
export const communityLiveQuery = z.object({ ticket: communityLiveTicketSchema });

/** PUT /api/communities/:communityId/mute — the caller mutes the community (no push, no email from it) or unmutes it. */
export const communityMuteBody = z.object({ muted: z.boolean() }).strict();

export type CommunityMuteBody = z.input<typeof communityMuteBody>;

/**
 * An announcement email's unsubscribe token: the community's id, the member's user id and a MAC
 * over both (hex), dot-separated. It names no address. The server checks it; this only bounds its
 * shape.
 */
export const communityEmailUnsubscribeTokenSchema = z.string().max(200).regex(/^[0-9a-f-]{36}\.[A-Za-z0-9_-]{1,64}\.[0-9a-f]{64}$/i);

/** The unsubscribe route's path: a public path (`AUTH_BYPASS_PATHS`), the address an email's one-click header names. */
export const COMMUNITY_EMAIL_UNSUBSCRIBE_PATH = '/api/communities/email-unsubscribe';

/**
 * POST /api/communities/email-unsubscribe?token=… — no sign-in: the token alone turns that
 * member's emails from that community off. The token rides the query, not a body, because mail
 * clients' one-click unsubscribe (RFC 8058) POSTs to the address in the email's headers with a
 * form body of their own, and the web page's button calls the same address.
 */
export const communityEmailUnsubscribeQuery = z.object({ token: communityEmailUnsubscribeTokenSchema });

export type CommunityEmailUnsubscribeQuery = z.input<typeof communityEmailUnsubscribeQuery>;

/**
 * The web app's page an announcement email's unsubscribe link opens, under the member's locale,
 * with `?token=`: it writes nothing until its button calls `POST /api/communities/email-unsubscribe`.
 */
export const COMMUNITY_EMAIL_UNSUBSCRIBE_PAGE_PATH = '/communities/unsubscribe';

// =============================================================================
// RESPONSE
// =============================================================================

/**
 * A community as its `CommunityDO` holds it. `org_name` is the owning org's name, copied when
 * the community is created.
 */
export interface CommunitySummary {
  community_id: string;
  org_id: string;
  org_name: string;
  name: string;
  created_at: string;
}

/** POST /api/admin/communities — 201. */
export interface AdminCreateCommunityResponse {
  data: CommunitySummary;
}

/**
 * DELETE /api/admin/communities/:communityId — 200: the community, and when it was destroyed
 * (the first destroy's time, when it was already destroyed). Its members' index rows go, and then
 * its storage, after the response.
 */
export interface AdminDestroyCommunityResponse {
  data: CommunitySummary & { destroyed_at: string };
}

/**
 * GET /api/communities/join-preview — what the join page shows. `avatar_url` and `rules` are null
 * until the community's settings set them. `flow_token` is a signed record of when this preview
 * was served, for the join (`communityJoinBody`); it is absent when the server holds no key to
 * sign it with.
 */
export interface CommunityJoinPreview {
  community_id: string;
  name: string;
  avatar_url: string | null;
  org_name: string;
  member_count: number;
  rules: string | null;
  flow_token?: string;
  /**
   * The community's admission, so the page says it is invite-only before sign-in (gh #5042).
   * Optional because it arrived after the field's first readers; the server always sets it.
   */
  admission?: CommunityAdmission;
}

export interface CommunityJoinPreviewResponse {
  data: CommunityJoinPreview;
}

/** The caller's membership, as a join answers it. */
export interface CommunityMembership {
  community_id: string;
  joined_at: string;
}

/** POST /api/communities/:communityId/join — 200, also for a member already in. */
export interface CommunityJoinResponse {
  data: CommunityMembership;
}

/**
 * The `details` of the 409 `COMMUNITY_NOT_INVITED_REASON` (gh #5042 Decision 7): `email` is the
 * address checked against the access list — the caller's own email as their sign-in provider
 * verified it, trimmed and lowercased — or null when it vouches for none (verify it first).
 */
export interface CommunityNotInvitedDetails {
  reason: typeof COMMUNITY_NOT_INVITED_REASON;
  email: string | null;
}

/**
 * A community's join link, as its org's owners and admins see it. `url` is null while the link
 * is off or was never made; replacing it makes a new one and turns it on.
 */
export interface CommunityJoinLink {
  enabled: boolean;
  url: string | null;
}

/** GET, POST and DELETE /api/communities/:communityId/join-link. */
export interface CommunityJoinLinkResponse {
  data: CommunityJoinLink;
}

/** A room, as its community's org admins manage it. */
export interface CommunityRoom {
  room_id: string;
  kind: CommunityRoomKind;
  name: string;
  /** An archived room takes no new message; its history stays readable. */
  archived: boolean;
  created_at: string;
}

/** GET /api/communities/:communityId/rooms — every room, archived ones included, oldest first. */
export interface CommunityRoomsResponse {
  data: CommunityRoom[];
}

/** POST /api/communities/:communityId/rooms — 201; PATCH …/rooms/:roomId — 200. */
export interface CommunityRoomResponse {
  data: CommunityRoom;
}

/**
 * A room as one reader sees it in their community's head: its last message's `seq` and theirs,
 * the last one they read. `last_seq - read_seq` is their unread count there
 * (`communityRoomUnreadCount`): it counts every message after their read position, those by
 * authors they block included (Decision 7), and their own sends move it forward.
 */
export interface CommunityRoomHead extends CommunityRoom {
  last_seq: number;
  read_seq: number;
}

/**
 * One of the caller's communities with its rooms: what the Community nav item, its switcher and
 * the community's header show. `member_count` is its active members; `role` is the caller's
 * standing there (owner-delegated decision 17), which decides what they may do: an `admin` (an
 * owner or admin of its org) and a `moderator` post in announcements, mark any post's answer and
 * moderate; a `member` posts where members post.
 */
export interface CommunityHead {
  community_id: string;
  name: string;
  avatar_url: string | null;
  org_id: string;
  org_name: string;
  member_count: number;
  role: CommunityViewerRole;
  /**
   * Whether the caller muted the community: no push and no email from it (`PUT …/mute`). Optional
   * because it arrived after the field's first readers; the server always sets it.
   */
  muted?: boolean;
  /**
   * The Courses tab (Decisions 25 and 30): set once the community has a published course, null
   * before. Optional because it arrived after the field's first readers; the server always sets it.
   */
  courses?: CommunityCoursesTab | null;
  rooms: CommunityRoomHead[];
}

/** GET /api/communities — the caller's communities (at most `COMMUNITIES_PER_PERSON_MAX`), by name. */
export interface CommunitiesResponse {
  data: CommunityHead[];
}

/**
 * The session snapshot's communities (Decision 7): the head of every community the viewer is an
 * active member of, empty when none. Absent from the snapshot while the communities surface is
 * off, for a caller that is not an interactive session, and when a head could not be read in
 * time (`GET /api/communities` serves the same list).
 */
export interface CommunitiesSessionSnapshot {
  heads: CommunityHead[];
}

/**
 * The author of a message, as every member sees them: a display name, never an email
 * (Decision 11). A null `display_name` renders as "WayAI user"; `deleted_user: true` as "Deleted
 * user" (Decision 12).
 */
export interface CommunityAuthor {
  user_id: string;
  display_name: string | null;
  deleted_user: boolean;
}

/**
 * A message's image, as its readers see it: its name as sent, the type read from that name, and
 * its size. Its bytes open only at a URL `POST /api/communities/:communityId/attachments/urls`
 * mints. A thread's file's shape.
 */
export type CommunityAttachment = PeopleThreadAttachment;

/**
 * One message. A deleted one stays as a tombstone: `body` (and a post's `title`) null, `deleted`
 * true, no `attachment` (Decision 12). `edited_at` is set once its author has changed its text or
 * title (Decision 28). `client_message_id` is the author's own idempotency key, shown to the author
 * alone. In a forum room a post has a `title`, and a reply the post's id as `parent_message_id`.
 * `attachment` is its image, null when it has none; beside one, `body` may be empty. The server
 * always sends it; it is optional so a client that predates images reads a message as before.
 */
export interface CommunityMessage {
  message_id: string;
  room_id: string;
  seq: number;
  author_user_id: string;
  body: string | null;
  deleted: boolean;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  client_message_id: string | null;
  title: string | null;
  parent_message_id: string | null;
  attachment?: CommunityAttachment | null;
}

/**
 * A page of messages, oldest first, without those of authors the reader blocks; `authors` names
 * the author of each one. `next_before_seq` continues toward older messages (`before`) and
 * `next_after_seq` toward newer ones (`after`); each is null when there are none that way. Both
 * come from every message the page read, those left out included, so a page can hold fewer than
 * `COMMUNITY_MESSAGES_PAGE_SIZE` messages, or none, and still continue.
 */
export interface CommunityMessagesPage {
  messages: CommunityMessage[];
  authors: CommunityAuthor[];
  next_before_seq: number | null;
  next_after_seq: number | null;
}

/** GET /api/communities/:communityId/rooms/:roomId/messages */
export interface CommunityMessagesResponse {
  data: CommunityMessagesPage;
}

/** POST …/messages (sent), PATCH and DELETE …/messages/:messageId — the message as it now stands. */
export interface CommunityMessageResponse {
  data: CommunityMessage;
}

/** POST …/rooms/:roomId/read — the room as the caller now sees it. */
export interface CommunityRoomHeadResponse {
  data: CommunityRoomHead;
}

/**
 * One image's URL: it opens the image until `expires_at` (`COMMUNITY_ATTACHMENT_URL_TTL_SECONDS`
 * after it was minted), for whoever holds it, while the communities surface is on.
 */
export type CommunityAttachmentUrl = PeopleThreadAttachmentUrl;

/**
 * POST /api/communities/:communityId/attachments/urls — a URL for each asked image the caller can
 * see in the community now; one they cannot (its message deleted, a reply whose post is deleted,
 * or not the community's) is left out.
 */
export interface CommunityAttachmentUrlsResponse {
  data: CommunityAttachmentUrl[];
}

/** What a forum post carries beyond a message (Decisions 26 and 29). */
export interface CommunityPostActivity {
  /** Its replies, deleted ones excluded, those of authors the reader blocks included. */
  reply_count: number;
  /** The `seq` of its latest activity: its newest reply's, or its own. Posts are listed by it. */
  last_activity_seq: number;
  /** When that activity was sent. */
  last_activity_at: string;
  /** Locked by a moderator: it takes no more replies. */
  locked: boolean;
}

/** A forum post with its whole text, as its replies page answers it. */
export interface CommunityPost extends CommunityMessage, CommunityPostActivity {
  title: string;
  /** The reply marked as its answer, or null (Decision 29). */
  answer_message_id: string | null;
}

/** A forum post as the posts page lists it: its title and the start of its text. */
export interface CommunityPostListing
  extends CommunityPostActivity, Pick<CommunityMessage, 'message_id' | 'room_id' | 'seq' | 'author_user_id' | 'created_at' | 'edited_at'> {
  title: string;
  /** The first `COMMUNITY_POST_PREVIEW_LENGTH` characters (code points) of its text. */
  preview: string;
  /** A reply is marked as its answer. */
  answered: boolean;
  /**
   * It has activity after the reader's read position in the room — since their last visit
   * (Decision 26). An org owner or admin who has not joined has no position: every post is new.
   */
  new: boolean;
}

/**
 * A page of posts, latest activity first, without those of authors the reader blocks; `authors`
 * names the author of each one. `next_before_activity` continues toward older activity
 * (`before_activity`), null when there is none; it comes from every post the page read, those
 * left out included, so a page can hold fewer than `COMMUNITY_POSTS_PAGE_SIZE`, or none, and still
 * continue. Posts are ordered by activity that moves: one replied to while a reader pages back
 * moves to the top, where the next newest page shows it.
 */
export interface CommunityPostsPage {
  posts: CommunityPostListing[];
  authors: CommunityAuthor[];
  next_before_activity: number | null;
}

/** GET /api/communities/:communityId/rooms/:roomId/posts */
export interface CommunityPostsResponse {
  data: CommunityPostsPage;
}

/**
 * A page of a post's replies (`messages`, paged as a room's messages are), with the post itself
 * and its answer, which the post shows first (Decision 29) and which also stays in its place among
 * the replies. `answer` is null when none is marked or when the reader blocks its author;
 * `authors` names the post's, the answer's and each reply's author.
 */
export interface CommunityRepliesPage extends CommunityMessagesPage {
  post: CommunityPost;
  answer: CommunityMessage | null;
}

/** GET /api/communities/:communityId/rooms/:roomId/posts/:postId/replies */
export interface CommunityRepliesResponse {
  data: CommunityRepliesPage;
}

/** PUT …/posts/:postId/answer — the post as it now stands. */
export interface CommunityPostResponse {
  data: CommunityPost;
}

/** Joins and the new accounts among them (`account_new`, Decision 28) over the last 7 and 30 days. */
export interface CommunityJoinCounts {
  joins_7d: number;
  joins_30d: number;
  new_accounts_7d: number;
  new_accounts_30d: number;
}

/** One join-link source tag's joins (`source` null: a join with none). */
export interface CommunitySourceStats extends CommunityJoinCounts {
  source: string | null;
}

/** One UTC day's messages, deleted ones included: chat and announcements, forum posts, forum replies. */
export interface CommunityDayStats {
  day: string;
  messages: number;
  posts: number;
  replies: number;
}

/**
 * A community's stats, as its org's owners and admins see them (Decision 28). A join is a
 * membership that started in the window; a person who joined again counts once, at their latest
 * join. Members active in the last 7 days are active members whose page reads stamped them seen
 * (at most once an hour).
 */
export interface CommunityStats {
  members: Record<CommunityMemberStatus, number>;
  active_members_7d: number;
  /** Every join, whatever its source. */
  joins: CommunityJoinCounts;
  /** The sources joined through in the last 30 days, most joins first, at most `COMMUNITY_STATS_SOURCES_MAX`. */
  joins_by_source: CommunitySourceStats[];
  /** The last `COMMUNITY_STATS_DAYS` UTC days, today included, oldest first. */
  days: CommunityDayStats[];
  /**
   * Course completions per lesson (Decision 28): every course, drafts included, by position (at
   * most `COMMUNITY_COURSES_MAX` courses and `COMMUNITY_LESSONS_MAX` lessons). Optional because it
   * arrived after the field's first readers; the server always sets it.
   */
  courses?: CommunityCourseStats[];
}

/** GET /api/communities/:communityId/stats */
export interface CommunityStatsResponse {
  data: CommunityStats;
}

/** A community's settings, as its org's owners and admins set them. */
export interface CommunitySettings {
  /** Shown on the join page; null for none. */
  rules: string | null;
  /** The seconds a member waits between two messages in one room; 0 is off. Moderators and admins are not held to it. */
  slow_mode_seconds: number;
  /**
   * The Courses tab's label, as typed; '' for the clients' locale default (Decision 30). Optional
   * because it arrived after the field's first readers; the server always sets it.
   */
  courses_label?: string;
  /** Who the link admits (gh #5042). Optional for the same reason; the server always sets it. */
  admission?: CommunityAdmission;
}

/** GET and PATCH /api/communities/:communityId/settings */
export interface CommunitySettingsResponse {
  data: CommunitySettings;
}

/** One email on a community's access list, as its org's owners and admins entered it (gh #5042). */
export interface CommunityAccessEntry {
  access_id: string;
  /** Trimmed and lowercased (`communityAccessEmailSchema`). */
  email: string;
  created_at: string;
}

/** A page of an access list, oldest first: `next_cursor` continues it, null at the end. */
export interface CommunityAccessPage {
  entries: CommunityAccessEntry[];
  next_cursor: string | null;
}

/** GET /api/communities/:communityId/access */
export interface CommunityAccessPageResponse {
  data: CommunityAccessPage;
}

/** What an add to an access list did: the entries it made, in the order their emails came, and how many of the distinct emails it was given the list already held. */
export interface CommunityAccessAdded {
  added: CommunityAccessEntry[];
  already_listed: number;
}

/** POST /api/communities/:communityId/access */
export interface CommunityAccessAddResponse {
  data: CommunityAccessAdded;
}

/**
 * A page of people (`COMMUNITY_PEOPLE_PAGE_SIZE`), by user id: a community's moderators, or the
 * people it bans. `next_after` continues it, null at the end.
 */
export interface CommunityPeoplePage {
  people: CommunityAuthor[];
  next_after: string | null;
}

/** GET /api/communities/:communityId/moderators, GET …/bans */
export interface CommunityPeopleResponse {
  data: CommunityPeoplePage;
}

/** PUT …/bans/:userId — the messages of theirs the ban deleted (0 unless it was asked to). */
export interface CommunityBanResponse {
  data: { messages_deleted: number };
}

/** A message or forum post its room pins, and since when. */
export interface CommunityPinnedMessage extends CommunityMessage {
  pinned_at: string;
}

/**
 * A room's pinned messages or posts (at most `COMMUNITY_PINS_PER_ROOM_MAX`), the latest pinned
 * first, without those of authors the reader blocks; `authors` names the author of each.
 */
export interface CommunityPinsPage {
  messages: CommunityPinnedMessage[];
  authors: CommunityAuthor[];
}

/** GET /api/communities/:communityId/rooms/:roomId/pins */
export interface CommunityPinsResponse {
  data: CommunityPinsPage;
}

/** PUT …/messages/:messageId/pin — whether the message is now pinned, and since when. */
export interface CommunityPinResponse {
  data: { message_id: string; pinned_at: string | null };
}

/**
 * One moderation, as the community's log keeps it: who did it (`actor_user_id`), what, to whom
 * (`target_user_id`: the person acted on, or the author of the message or post), where (`room_id`,
 * `message_id`) and when. `messages_deleted` is a ban's count of the messages it deleted (null
 * when it was not asked to delete them; null on every other action).
 */
export interface CommunityModAction {
  action_id: number;
  action: CommunityModActionKind;
  actor_user_id: string;
  target_user_id: string | null;
  room_id: string | null;
  message_id: string | null;
  messages_deleted: number | null;
  created_at: string;
}

/**
 * A page of the moderation log, newest first; `people` names each entry's actor and target.
 * `next_before` continues toward older entries, null at the end.
 */
export interface CommunityModLogPage {
  actions: CommunityModAction[];
  people: CommunityAuthor[];
  next_before: number | null;
}

/** GET /api/communities/:communityId/mod-log */
export interface CommunityModLogResponse {
  data: CommunityModLogPage;
}

/**
 * A report in the moderators' inbox: what it is about and why, never who filed it. A message's
 * report keeps the message as its reporter saw it (`body`, a post's `title`, and its image's name
 * and type as `attachment`), so a later edit cannot erase it (Decision 28). `resolved_at` is when a
 * moderator or admin marked it handled.
 */
export interface CommunityReport {
  report_id: string;
  target: CommunityReportTarget;
  /** The person it is about: the message's author, or the person reported. */
  subject_user_id: string;
  room_id: string | null;
  message_id: string | null;
  body: string | null;
  title: string | null;
  /**
   * The reported message's image by name and type, never its bytes; null for a message with none,
   * a report about a person, and a report filed before the inbox kept images. The server always
   * sends it; it is optional so a client that predates it reads a report as before.
   */
  attachment?: PeopleReportAttachment | null;
  reason: PeopleReportReason;
  /** The reporter's own note. */
  details: string | null;
  created_at: string;
  resolved_at: string | null;
}

/**
 * A page of the inbox, newest first; `people` names each report's subject. `next_before` continues
 * toward older reports, null at the end. A page may hold fewer than `COMMUNITY_REPORTS_PAGE_SIZE`
 * reports, as the reports about a moderator or an owner or admin of the org are left out.
 */
export interface CommunityReportsPage {
  reports: CommunityReport[];
  people: CommunityAuthor[];
  next_before: number | null;
}

/** GET /api/communities/:communityId/reports */
export interface CommunityReportsResponse {
  data: CommunityReportsPage;
}

/**
 * GET /api/communities/orgs/:orgId — the communities the org owns (at most
 * `COMMUNITIES_PER_ORG_MAX`), oldest first, for its owners and admins; never one that no longer
 * exists.
 */
export interface CommunityOrgCommunitiesResponse {
  data: CommunitySummary[];
}

/** POST /api/communities/:communityId/live-ticket — a single-use ticket for the community's live socket. */
export interface CommunityLiveTicket {
  ticket: string;
  /** The seconds it stays usable (`COMMUNITY_LIVE_TICKET_TTL_SECONDS`). */
  expires_in: number;
}

export interface CommunityLiveTicketResponse {
  data: CommunityLiveTicket;
}

/** PUT /api/communities/:communityId/mute — whether the caller has the community muted now. */
export interface CommunityMuteResponse {
  data: { muted: boolean };
}

// =============================================================================
// PUSH (Decisions 8 and 28) — a community's push to a member's phone, sent only to tokens
// registered with `supports_communities`. Its `data` never carries a `conversation_id` or a
// `thread_id`, so a build that reads only those resolves a tap to no navigation.
// =============================================================================

/** The `kind` of an announcement's push `data`. */
export const COMMUNITY_ANNOUNCEMENT_PUSH_KIND = 'community_announcement';
/** The `kind` of the push a forum post's author gets about a reply. */
export const COMMUNITY_REPLY_PUSH_KIND = 'community_reply';

/** An announcement's push `data`: the room and the message to open. String-valued, as push data is. */
export type CommunityAnnouncementPushData = {
  kind: typeof COMMUNITY_ANNOUNCEMENT_PUSH_KIND;
  community_id: string;
  room_id: string;
  message_id: string;
};

/** A reply's push `data`: the forum room, the post to open, and the reply. String-valued. */
export type CommunityReplyPushData = {
  kind: typeof COMMUNITY_REPLY_PUSH_KIND;
  community_id: string;
  room_id: string;
  post_id: string;
  message_id: string;
};

export type CommunityPushData = CommunityAnnouncementPushData | CommunityReplyPushData;

// =============================================================================
// LIVE FRAMES (Decision 6) — what a community's live socket sends, one JSON frame per change, in
// the order the changes were made. Nothing is sent per member: a frame reaches the sockets open on
// the community at the time. A message frame carries its message as every member reads it, a
// tombstone's text erased; its author's idempotency key (`client_message_id`) reaches only the
// author's own sockets.
// =============================================================================

/** A forum post as a change leaves it: what its listing and its page show beyond its text. */
export interface CommunityPostState extends CommunityPostActivity {
  message_id: string;
  answer_message_id: string | null;
}

/** What every frame says: where the change happened, and whose content it changed. */
export interface CommunityLiveFrameBase {
  community_id: string;
  room_id: string;
  /**
   * The author of the content the frame is about — the message's, or for a lock or an answer the
   * post's — so a client drops the frames of the authors it blocks (Decision 10).
   */
  author_user_id: string;
}

/** A frame about one message: the message as every member reads it, and `authors` naming its author. */
export interface CommunityMessageFrameBase extends CommunityLiveFrameBase {
  message: CommunityMessage;
  authors: CommunityAuthor[];
}

/**
 * `community_message` — a message, forum post or reply was sent. `post` is a reply's post as the
 * reply leaves it (its count and latest activity), null for anything else.
 */
export interface CommunityMessageFrame extends CommunityMessageFrameBase {
  post: CommunityPostState | null;
}

/** `community_message_edited` — its author changed a message's text, or a post's title. */
export type CommunityMessageEditedFrame = CommunityMessageFrameBase;

/**
 * `community_message_deleted` — a message was deleted, by its author, a moderator or the WayAI team:
 * `message` is its tombstone, which is no longer pinned. A deleted post's replies stop showing.
 * `post` is a deleted reply's post as it leaves it (one reply fewer, and no answer when the reply
 * was it); null for anything else, and when that post is deleted too.
 */
export interface CommunityMessageDeletedFrame extends CommunityMessageFrameBase {
  post: CommunityPostState | null;
}

/**
 * `community_messages_deleted` — a ban deleted every message `author_user_id` wrote in the room, up
 * to `through_seq`, at once. The posts their replies were under lose those replies from their counts
 * and answers, which a client showing the room's posts reads again with its page.
 */
export interface CommunityMessagesDeletedFrame extends CommunityLiveFrameBase {
  through_seq: number;
}

/** `community_message_pinned` — a moderator or admin pinned a message or post (since `pinned_at`) or unpinned it (null). */
export interface CommunityMessagePinnedFrame extends CommunityMessageFrameBase {
  pinned_at: string | null;
}

/**
 * `community_post_updated` — a post was locked or unlocked, or its answer was marked, changed or
 * cleared: `post` as it now stands, and `answer` the reply it now marks (null for none). The
 * answer's author is on it: a client that blocks them hides it, as the replies page does. `authors`
 * names the post's author and the answer's.
 */
export interface CommunityPostUpdatedFrame extends CommunityLiveFrameBase {
  post: CommunityPostState;
  answer: CommunityMessage | null;
  authors: CommunityAuthor[];
}

/** Each live frame type of a community's socket, and the payload it carries. */
export interface CommunityLiveFramePayloads {
  community_message: CommunityMessageFrame;
  community_message_edited: CommunityMessageEditedFrame;
  community_message_deleted: CommunityMessageDeletedFrame;
  community_messages_deleted: CommunityMessagesDeletedFrame;
  community_message_pinned: CommunityMessagePinnedFrame;
  community_post_updated: CommunityPostUpdatedFrame;
}

/** A frame of a community's live socket, as JSON: its type, and the payload that type carries. */
export type CommunityLiveFrame = {
  [Type in keyof CommunityLiveFramePayloads]: { type: Type; payload: CommunityLiveFramePayloads[Type] };
}[keyof CommunityLiveFramePayloads];

/** A room's unread count for its reader: every message after their read position. */
export function communityRoomUnreadCount(room: Pick<CommunityRoomHead, 'last_seq' | 'read_seq'>): number {
  return Math.max(0, room.last_seq - room.read_seq);
}

/** A community's unread count for its reader, the Community badge's share of it: its rooms not archived. */
export function communityUnreadCount(head: Pick<CommunityHead, 'rooms'>): number {
  return head.rooms.reduce((sum, room) => sum + (room.archived ? 0 : communityRoomUnreadCount(room)), 0);
}
